/**
 * Multi-Platform Video Learning Types
 * Supports: YouTube, TikTok, Instagram, Facebook, X (Twitter)
 */

export type VideoPlatform = 'youtube' | 'tiktok' | 'instagram' | 'facebook' | 'x';

export interface VideoCaption {
    /** Start time in seconds */
    start: number;
    /** Duration in seconds */
    duration: number;
    /** Caption text */
    text: string;
    /** Vietnamese translation (populated by AI at add-time) */
    translation?: string;
}

export interface Video {
    /** Database UUID */
    id: string;
    /** Platform-specific video ID */
    videoId: string;
    /** Platform type */
    platform: VideoPlatform;
    /** Original video URL */
    url: string;
    /** Video title */
    title: string;
    /** Video description */
    description?: string;
    /** Video duration in seconds */
    duration?: number;
    /** Video thumbnail URL */
    thumbnailUrl?: string;
    /** Array of captions/subtitles */
    captions: VideoCaption[];
    /** Language of captions */
    language?: string;
    /** Status */
    status: 'active' | 'hidden';
    /** Created timestamp */
    createdAt?: string;
    /** Category ID referencing categories table */
    categoryId?: string;
    /** Category name for display */
    categoryName?: string;
    /** AI-detected CEFR level */
    certLevel?: string;
}

export interface VideoLearningState {
    /** Current video being studied */
    video: Video | null;
    /** Currently selected caption for practice */
    selectedCaptionIndex: number | null;
    /** Current playback time */
    currentTime: number;
    /** Is video playing */
    isPlaying: boolean;
    /** User's recording for current caption */
    userRecording: Blob | null;
}

/**
 * Detect platform from URL
 */
export function detectPlatform(url: string): VideoPlatform | null {
    const platformPatterns: { platform: VideoPlatform; patterns: RegExp[] }[] = [
        {
            platform: 'youtube',
            patterns: [
                /youtube\.com\/watch/,
                /youtu\.be\//,
                /youtube\.com\/embed\//,
                /youtube\.com\/shorts\//,
            ],
        },
        {
            platform: 'tiktok',
            patterns: [
                /tiktok\.com\/@[^/]+\/video\//,
                /tiktok\.com\/t\//,
                /vm\.tiktok\.com\//,
            ],
        },
        {
            platform: 'instagram',
            patterns: [
                /instagram\.com\/reels?\//,
                /instagram\.com\/p\//,
                /instagram\.com\/tv\//,
            ],
        },
        {
            platform: 'facebook',
            patterns: [
                /facebook\.com\/[^/]+\/videos\//,
                /facebook\.com\/watch\//,
                /facebook\.com\/reel\//,
                /fb\.watch\//,
            ],
        },
        {
            platform: 'x',
            patterns: [
                /x\.com\/[^/]+\/status\//,
                /twitter\.com\/[^/]+\/status\//,
            ],
        },
    ];

    for (const { platform, patterns } of platformPatterns) {
        for (const pattern of patterns) {
            if (pattern.test(url)) {
                return platform;
            }
        }
    }

    return null;
}

/**
 * Extract video ID from URL based on platform
 */
export function extractVideoId(url: string, platform?: VideoPlatform): string | null {
    const detectedPlatform = platform || detectPlatform(url);

    if (!detectedPlatform) return null;

    const extractors: Record<VideoPlatform, RegExp[]> = {
        youtube: [
            /(?:youtube\.com\/watch\?v=|youtube\.com\/watch\?.+&v=)([^&]+)/,
            /youtu\.be\/([^?]+)/,
            /youtube\.com\/embed\/([^?]+)/,
            /youtube\.com\/shorts\/([^?]+)/,
        ],
        tiktok: [
            /tiktok\.com\/@[^/]+\/video\/(\d+)/,
            /tiktok\.com\/t\/([^/?]+)/,
            /vm\.tiktok\.com\/([^/?]+)/,
        ],
        instagram: [
            /instagram\.com\/(?:reels?|p|tv)\/([^/?]+)/,
        ],
        facebook: [
            /facebook\.com\/[^/]+\/videos\/(\d+)/,
            /facebook\.com\/watch\/?\?v=(\d+)/,
            /facebook\.com\/reel\/(\d+)/,
        ],
        x: [
            /(?:x|twitter)\.com\/[^/]+\/status\/(\d+)/,
        ],
    };

    const patterns = extractors[detectedPlatform];
    for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match && match[1]) {
            return match[1];
        }
    }

    return null;
}

/**
 * Get thumbnail URL for a video
 */
export function getVideoThumbnail(
    videoId: string,
    platform: VideoPlatform,
    quality: 'default' | 'medium' | 'high' | 'maxres' = 'high'
): string {
    if (platform === 'youtube') {
        const qualityMap = {
            default: 'default',
            medium: 'mqdefault',
            high: 'hqdefault',
            maxres: 'maxresdefault',
        };
        return `https://img.youtube.com/vi/${videoId}/${qualityMap[quality]}.jpg`;
    }

    // For other platforms, thumbnails come from the API
    return '';
}

/**
 * Format seconds to mm:ss or hh:mm:ss
 */
export function formatTimestamp(seconds: number): string {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hrs > 0) {
        return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Get platform display name
 */
export function getPlatformDisplayName(platform: VideoPlatform): string {
    const names: Record<VideoPlatform, string> = {
        youtube: 'YouTube',
        tiktok: 'TikTok',
        instagram: 'Instagram',
        facebook: 'Facebook',
        x: 'X',
    };
    return names[platform];
}

/**
 * Get platform color for styling
 */
export function getPlatformColor(platform: VideoPlatform): string {
    const colors: Record<VideoPlatform, string> = {
        youtube: '#FF0000',
        tiktok: '#00F2EA',
        instagram: '#E4405F',
        facebook: '#1877F2',
        x: '#000000',
    };
    return colors[platform];
}

/**
 * Get embed URL for a video based on platform
 * @param videoId - Platform-specific video ID
 * @param platform - Video platform
 * @param originalUrl - Original video URL (needed for some platforms like Facebook)
 * @returns Embed-ready URL or null if platform doesn't support embedding
 */
export function getEmbedUrl(
    videoId: string,
    platform: VideoPlatform,
    originalUrl?: string
): string | null {
    switch (platform) {
        case 'youtube':
            return `https://www.youtube.com/embed/${videoId}?enablejsapi=1&rel=0&modestbranding=1`;

        case 'tiktok':
            return `https://www.tiktok.com/embed/v2/${videoId}`;

        case 'instagram':
            return `https://www.instagram.com/reel/${videoId}/embed`;

        case 'facebook':
            if (originalUrl) {
                const encodedUrl = encodeURIComponent(originalUrl);
                return `https://www.facebook.com/plugins/video.php?href=${encodedUrl}&show_text=false&width=560`;
            }
            return null;

        case 'x':
            // X/Twitter embeds require the Twitter widget JS, we'll handle this differently
            if (originalUrl) {
                return `https://platform.twitter.com/embed/Tweet.html?id=${videoId}`;
            }
            return null;

        default:
            return null;
    }
}

/**
 * Check if a platform supports time synchronization for captions
 */
export function supportsTimeSync(platform: VideoPlatform): boolean {
    // Only YouTube supports full time sync via IFrame API
    return platform === 'youtube';
}

/**
 * Get platform-specific player configuration
 */
export function getPlatformPlayerConfig(platform: VideoPlatform): {
    aspectRatio: string;
    allowFullscreen: boolean;
    minHeight: number;
} {
    switch (platform) {
        case 'tiktok':
        case 'instagram':
            // Vertical video format (9:16)
            return {
                aspectRatio: '9/16',
                allowFullscreen: true,
                minHeight: 500,
            };
        case 'youtube':
        case 'facebook':
        case 'x':
        default:
            // Horizontal video format (16:9)
            return {
                aspectRatio: '16/9',
                allowFullscreen: true,
                minHeight: 315,
            };
    }
}

/**
 * Video item that needs review/practice
 */
export interface VideoReviewItem {
    /** Database video ID */
    videoId: string;
    /** Video title */
    title: string;
    /** Thumbnail URL */
    thumbnailUrl?: string;
    /** Last time this video was practiced */
    lastPracticedAt: string | null;
    /** Video platform */
    platform: VideoPlatform;
}

/**
 * One caption line matched by the "in context" search.
 *
 * This is a *clip*, not a video: several hits can come from the same video, and
 * the only thing that makes a hit playable is `start`/`end`. `videoRowId` is the
 * `videos.id` UUID (what `/videos/:videoId` resolves), while `videoId` is the
 * platform's own id (what an embed needs).
 */
export interface VideoCaptionHit {
    /** `videos.id` — the UUID the detail route resolves */
    videoRowId: string;
    /**
     * Which corpus the clip came from. `library` is the curated, published
     * video library; `imported` is the YouTube channel importer's corpus.
     * A clip plays the same way either way — this only decides the badge.
     */
    source: "library" | "imported";
    /** Platform-specific video ID (the embed id) */
    videoId: string;
    platform: VideoPlatform;
    title: string;
    videoUrl: string;
    thumbnailUrl?: string;
    duration?: number;
    /** When the matched line starts, in seconds */
    start: number;
    /** When the matched line stops, in seconds */
    end: number;
    /** The spoken line, as stored in the transcript */
    text: string;
    /** Full-text relevance score from the search RPC */
    rank: number;
}

/**
 * Caption search page result. `total` counts *clips after per-video
 * de-duplication*, so it is the number of rows the reader can page through.
 */
export interface VideoCaptionSearchResult {
    hits: VideoCaptionHit[];
    total: number;
}

/**
 * Combined review reminder data
 */
export interface ReviewReminderData {
    /** Number of vocabulary words due for review */
    vocabularyDue: number;
    /** Videos that need practice */
    videosDue: VideoReviewItem[];
    /** Whether data is still loading */
    isLoading: boolean;
}

